import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import {
  ColumnsButton,
  ColumnsDrawer,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  IconButton,
  ListView,
  ListViewActions,
  Page,
  SearchInput,
} from '~/components';
import { ClientFiltersBar, ClientFiltersGroup, FilterButton, FiltersDrawer } from '~/components/filters';
import { useApi, useConfirmation, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useFeatures,
  useIsMounted,
  useSearchParams,
  useSearchParamsConfig,
  useViewColumns,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { mimeTypes, QuerySort } from '~/utils';
import ClientRow from './ClientRow';
import { ClientDrawer } from './client-drawer';

function ClientsListPage() {
  const documentTitle = useDocumentTitle('Clients');

  const { workspace } = useWorkspace();
  const api = useApi();

  const isMounted = useIsMounted();

  const searchParamsConfig = useSearchParamsConfig();
  const clientFilters = useClientFilters({
    clientRecordStatusId: { default: 'active', ...searchParamsConfig.clientRecordStatusId },
  });

  const history = useHistory();
  const { clientId } = useParams();
  const location = useLocation();
  const { url } = useRouteMatch();

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      q: '',
      page: 0,
      size: 25,
      sort: new QuerySort('client.name', 'asc'),
      ...clientFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const load = () => {
    setQuery((state) => ({
      ...state,
      params: {
        ...state.params,
        page: 0,
      },
      action: 'load',
    }));
  };

  const loadMore = useCallback(() => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      ) {
        return state;
      }

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  }, []);

  const removeItem = (id) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.filter((i) => i.id !== id),
        total: state.data.total - 1,
      },
    }));
  };

  const updateItem = (item) => {
    setQuery((state) => ({
      ...state,
      data: {
        ...state.data,
        results: state.data?.results.map((i) => (i.id === item.id ? { ...i, ...item } : i)),
      },
    }));
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const confirmation = useConfirmation();

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        q: { default: '' },
        sort: { default: new QuerySort('client.name', 'asc'), ...searchParamsConfig.sort },
        ...clientFilters.searchParamsConfig,
      }),
      [clientFilters, searchParamsConfig],
    ),
    sessionKey: 'clients_list',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  const urlSearchParams = useMemo(
    () => ({
      q: params.q,
      sort: params.sort,
      page: params.page,
      size: params.size,
      ...clientFilters.mapUrlSearchParams(params),
    }),
    [clientFilters, params],
  );

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www.workspaces(workspace.id).clients().get(urlSearchParams);

      if (!isMounted.current) return;

      setQuery((state) => ({
        ...state,
        action: null,
        dialog: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
    }
  }, [workspace.id, api, urlSearchParams, isMounted]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const handleFilter = (values) => {
    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
  };

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
  };

  async function handleSaved() {
    load();
  }

  function handleClose() {
    history.push({ pathname: `/app/${workspace.key}/clients`, search: location.search });
    documentTitle.set('Clients');
  }

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .clients()
          .export(urlSearchParams, {
            headers: { accept: mimeType },
            responseType: 'blob',
          })}
        onClose={resolve}
      />
    ));
  };

  const [dialog, setDialog] = useState(null);
  const closeDialog = () => setDialog(null);
  const showFilters = () => setDialog(<Filters values={params} onApply={handleFilter} onClose={closeDialog} />);
  const features = useFeatures();

  const columns = useViewColumns({
    storageKey: 'clients_list_columns',
    columns: useMemo(
      () =>
        _.compact([
          { key: 'name', name: 'Name', disabled: true },
          { key: 'code', name: 'Code', default: true },
          { key: 'owner', name: 'Relationship Owner', default: true },
          { key: 'salesRepresentative', name: 'Sales Representative', default: true },
          { key: 'industry', name: 'Industry', default: false },
          features.practices && { key: 'practice', name: 'Practice', default: false },
          features.businessUnits && { key: 'businessUnit', name: 'Business Unit', default: false },
          { key: 'location', name: 'Location', default: false },
          { key: 'currency', name: 'Currency', default: false },
          { key: 'createdAt', name: 'Created', default: true },
        ]),
      [features],
    ),
  });

  const handleShowColumnsDrawer = () => {
    setDialog(
      <ColumnsDrawer
        value={columns.selected}
        columns={columns.all}
        onClose={closeDialog}
        onApply={(value) => {
          columns.save(value);
          closeDialog();
        }}
      />,
    );
  };

  const auth = useAuth();

  if (!isReady && !data) return <PageLoader />;

  return (
    <>
      <Page scrollable>
        <Page.Header>
          <Page.Title>Clients</Page.Title>

          <Page.Actions>
            <ExportDropdown>
              {({ setIsOpen }) => (
                <>
                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`clients.csv`, mimeTypes.csv);
                      setIsOpen(false);
                    }}>
                    Export to CSV
                  </ExportDropdown.Item>

                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`clients.xlsx`, mimeTypes.xlsx);
                      setIsOpen(false);
                    }}>
                    Export to Excel
                  </ExportDropdown.Item>
                </>
              )}
            </ExportDropdown>

            <ColumnsButton isOutline onClick={handleShowColumnsDrawer} />

            <FilterButton isOutline onClick={showFilters} />

            <IconButton
              tooltip={auth.clients.create ? 'New Client' : 'Your security role prohibits you from creating clients.'}
              icon="plus"
              onClick={() => history.push(`${url}/new${location.search}`)}
              disabled={!auth.clients.create}
            />
          </Page.Actions>
        </Page.Header>

        <Page.Filters>
          <FiltersBar>
            <SearchInput
              placeholder="Search"
              onChange={({ target: { name, value } }) => handleFilter({ [name]: value })}
              value={params.q}
            />

            <ClientFiltersBar
              filters={params}
              sticky={['clientRecordStatusId', 'clientOwners', 'clientPractices', 'clientTags']}
              onChange={handleFilter}
            />
          </FiltersBar>
        </Page.Filters>

        <Page.ListView>
          <ListView>
            <ListView.Header>
              <ListView.Column sticky name="name" minWidth="16rem" onSort={handleSort} sort={params.sort}>
                Name
              </ListView.Column>
              <ListView.Column
                name="client.code"
                minWidth="16rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.code}>
                Code
              </ListView.Column>
              <ListView.Column
                name="owner.name"
                minWidth="16rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.owner}>
                Relationship Owner
              </ListView.Column>
              <ListView.Column
                name="salesRepresentative.name"
                minWidth="16rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.salesRepresentative}>
                Sales Representative
              </ListView.Column>
              <ListView.Column
                name="industry.name"
                minWidth="12rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.industry}>
                Industry
              </ListView.Column>
              <ListView.Column
                name="practice.name"
                minWidth="12rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={features.practices && columns.visible.practice}>
                Practice
              </ListView.Column>
              <ListView.Column
                name="businessUnit.name"
                minWidth="12rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={features.businessUnits && columns.visible.businessUnit}>
                Business Unit
              </ListView.Column>
              <ListView.Column
                name="location.name"
                minWidth="12rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.location}>
                Location
              </ListView.Column>
              <ListView.Column
                name="client.currency"
                minWidth="8rem"
                onSort={handleSort}
                sort={params.sort}
                isVisible={columns.visible.currency}>
                Currency
              </ListView.Column>
              <ListView.Column
                name="client.createdAt"
                onSort={handleSort}
                sort={params.sort}
                width="8rem"
                align="right"
                isVisible={columns.visible.createdAt}>
                Created
              </ListView.Column>
              <ListViewActions.Column />
            </ListView.Header>
            <ListView.Body fade={action === 'filter'}>
              {!data.total && <ListView.Empty />}

              {data.results.map((item) => (
                <ClientRow
                  key={item.id}
                  client={item}
                  onSaved={updateItem}
                  onDeleted={removeItem}
                  onCollapse={updateItem}
                />
              ))}

              {data.total > data.results.length && (
                <ListView.Loader key={data.results.length} onIntersecting={loadMore} />
              )}
            </ListView.Body>

            <ListView.Status total={data.total} label="Client" isLoading={!!action} />
          </ListView>
        </Page.ListView>
      </Page>

      {clientId && (
        <ClientDrawer clientId={clientId} onSaved={handleSaved} onDeleted={removeItem} onClose={handleClose} />
      )}

      {dialog}
    </>
  );
}

function Filters({ values, onClose, onApply }) {
  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen onApply={handleApply} onClose={handleCancel}>
      <ClientFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}

export default ClientsListPage;
export { ClientsListPage };
